import React, { useState, useEffect } from 'react';
import type { Campaign, CampaignCreative } from '../campaignsApi';
import { VideoStudioPanel } from './VideoStudioPanel';
import { Film, CheckCircle2, AlertCircle, Sparkles, Layers } from 'lucide-react';

interface CampaignVideoStudioTabProps {
  campaign: Campaign;
  creatives: CampaignCreative[];
  selectedCreativeId?: string | null;
  onSelectCreativeId?: (id: string) => void;
  isLight: boolean;
  onNavigateToMatrix: () => void;
}

const LEVEL_MAP: Record<string, { label: string; stage: string; color: string; bgLight: string; bgDark: string }> = {
  unaware: { label: 'Inconsciente', stage: 'Nível 1 • Topo de Funil', color: '#6366f1', bgLight: '#eef2ff', bgDark: 'rgba(99,102,241,0.15)' },
  problem_aware: { label: 'Consciente do Problema', stage: 'Nível 2 • Descoberta', color: '#ec4899', bgLight: '#fdf2f8', bgDark: 'rgba(236,72,153,0.15)' },
  solution_aware: { label: 'Consciente da Solução', stage: 'Nível 3 • Meio de Funil', color: '#8b5cf6', bgLight: '#f5f3ff', bgDark: 'rgba(139,92,246,0.15)' },
  product_aware: { label: 'Consciente do Produto', stage: 'Nível 4 • Consideração', color: '#3b82f6', bgLight: '#eff6ff', bgDark: 'rgba(59,130,246,0.15)' },
  most_aware: { label: 'Totalmente Consciente', stage: 'Nível 5 • Fundo / Decisão', color: '#10b981', bgLight: '#f0fdf4', bgDark: 'rgba(16,185,129,0.15)' },
};

export const CampaignVideoStudioTab: React.FC<CampaignVideoStudioTabProps> = ({
  campaign,
  creatives,
  selectedCreativeId,
  onSelectCreativeId,
  isLight,
  onNavigateToMatrix,
}) => {
  const [activeCreativeId, setActiveCreativeId] = useState<string>(() => {
    if (selectedCreativeId && creatives.some((c) => c.id === selectedCreativeId)) {
      return selectedCreativeId;
    }
    return creatives[0]?.id || '';
  });

  useEffect(() => {
    if (selectedCreativeId && creatives.some((c) => c.id === selectedCreativeId)) {
      setActiveCreativeId(selectedCreativeId);
    } else if (!activeCreativeId && creatives.length > 0) {
      setActiveCreativeId(creatives[0].id);
    }
  }, [selectedCreativeId, creatives, activeCreativeId]);

  const handleSelect = (id: string) => {
    setActiveCreativeId(id);
    onSelectCreativeId?.(id);
  };

  const activeCreative = creatives.find((c) => c.id === activeCreativeId);

  if (!creatives || creatives.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3.5rem 1.5rem', background: isLight ? '#f8fafc' : '#14141c', borderRadius: '16px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)' }}>
        <Film size={44} style={{ color: '#6366f1', margin: '0 auto 1rem', opacity: 0.8 }} />
        <h3 style={{ fontSize: '1.2rem', color: isLight ? '#0f172a' : '#fff', fontWeight: 700, margin: '0 0 0.5rem 0' }}>
          Nenhum anúncio gerado para a campanha
        </h3>
        <p style={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '0.9rem', maxWidth: '480px', margin: '0 auto 1.5rem' }}>
          Gere primeiro as cópias e propostas na Matriz Narrativa para que os 5 níveis de consciência sirvam como roteiro cinematográfico para os vídeos.
        </p>
        <button
          type="button"
          onClick={onNavigateToMatrix}
          className="primary-button"
          style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Sparkles size={16} /> Ir para a Matriz Narrativa
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%' }}>
      {/* Top Header Card */}
      <div style={{
        background: isLight ? 'linear-gradient(135deg, #eff6ff, #f5f3ff)' : 'linear-gradient(135deg, rgba(37,99,235,0.12), rgba(139,92,246,0.1))',
        border: isLight ? '1px solid #dbeafe' : '1px solid rgba(99,102,241,0.25)',
        borderRadius: '14px',
        padding: '1.25rem 1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
            <Film size={20} style={{ color: '#3b82f6' }} />
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: isLight ? '#1e3a8a' : '#e0e7ff' }}>
              Estúdio de Vídeo da Campanha (Studio Flow & Veo 3.1)
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: '0.85rem', color: isLight ? '#475569' : '#94a3b8' }}>
            Selecione um dos 5 níveis de consciência da campanha para editar o roteiro, aprovar cenas take-by-take e renderizar versões em vídeo.
          </p>
        </div>
        <button
          type="button"
          onClick={onNavigateToMatrix}
          style={{
            background: isLight ? '#ffffff' : 'rgba(255,255,255,0.08)',
            border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
            color: isLight ? '#334155' : '#cbd5e1',
            borderRadius: '8px',
            padding: '0.45rem 0.85rem',
            fontSize: '0.78rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
          }}
        >
          <Layers size={14} /> Ver Matriz Narrativa (Textos & Banners)
        </button>
      </div>

      {/* Seletor Horizontal de Criativos / Níveis de Consciência */}
      <div>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#475569' : '#94a3b8', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Selecione o Roteiro para Produção de Vídeo:
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '0.75rem',
        }}>
          {creatives.map((c, idx) => {
            const isSelected = c.id === activeCreativeId;
            const levelInfo = (c.consciousnessLevel && LEVEL_MAP[c.consciousnessLevel]) || {
              label: c.angleName || `Ângulo #${idx + 1}`,
              stage: `Ângulo ${idx + 1}`,
              color: '#6366f1',
              bgLight: '#eef2ff',
              bgDark: 'rgba(99,102,241,0.15)',
            };
            const hasVideo = !!c.videoUrl;

            return (
              <div
                key={c.id}
                onClick={() => handleSelect(c.id)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '12px',
                  padding: '0.85rem 1rem',
                  transition: 'all 0.18s ease',
                  background: isSelected
                    ? (isLight ? '#ffffff' : '#1e1b4b')
                    : (isLight ? '#f8fafc' : '#13131a'),
                  border: isSelected
                    ? `2px solid ${levelInfo.color}`
                    : (isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)'),
                  boxShadow: isSelected
                    ? `0 4px 14px ${levelInfo.color}33`
                    : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.45rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: levelInfo.color,
                    background: isLight ? levelInfo.bgLight : levelInfo.bgDark,
                    padding: '0.15rem 0.45rem',
                    borderRadius: '6px',
                  }}>
                    {levelInfo.stage}
                  </span>
                  {hasVideo ? (
                    <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                      <CheckCircle2 size={12} /> Vídeo
                    </span>
                  ) : c.videoStatus === 'generating' ? (
                    <span style={{ fontSize: '0.68rem', color: '#3b82f6', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                      ⏳ Renderizando
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.68rem', color: isLight ? '#94a3b8' : '#64748b' }}>
                      ⚪ Sem vídeo
                    </span>
                  )}
                </div>

                <div
                  title={c.angleName || levelInfo.label}
                  style={{
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    color: isLight ? '#0f172a' : '#f8fafc',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: '1.25',
                    minHeight: '2rem',
                  }}
                >
                  {c.angleName || levelInfo.label}
                </div>

                <div
                  title={c.headline || c.primaryText || ''}
                  style={{
                    fontSize: '0.72rem',
                    color: isLight ? '#64748b' : '#94a3b8',
                    lineHeight: '1.3',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: '2.8rem',
                  }}
                >
                  {c.headline || c.primaryText || 'Sem descrição'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Embedded Video Studio Panel */}
      {activeCreative && (
        <div style={{ width: '100%' }}>
          <VideoStudioPanel
            key={`${campaign.id}-${activeCreative.id}`}
            isOpen={true}
            embedded={true}
            campaignId={campaign.id}
            creativeId={activeCreative.id}
            creativeTitle={activeCreative.angleName || activeCreative.name || activeCreative.headline || 'Criativo'}
          />
        </div>
      )}
    </div>
  );
};
export default CampaignVideoStudioTab;
